/**
 * Economic news calendar state — shared by the News drawer AND the chart
 * overlay.
 *
 * The payload lives here (rather than in the panel's local state) because the
 * chart overlay needs the same events while the drawer is CLOSED. One fetch
 * feeds both surfaces.
 *
 * Filter prefs + the chart-overlay toggle persist to localStorage, like the
 * app theme and trade volume: they're viewing preferences that should follow
 * the browser, not workspace content.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type { NewsPayload, NewsSourceId } from "@/api/types";

const PREFS_KEY = "tickview.news.filters";

export type ImpactFloor = "all" | "low" | "medium" | "high";

export interface NewsFilters {
  source: NewsSourceId;
  impact: ImpactFloor;
  /** Empty = all currencies. */
  currencies: string[];
  restrictedOnly: boolean;
}

export const DEFAULT_FILTERS: NewsFilters = {
  source: "ftmo",
  impact: "medium",
  currencies: [],
  // Derived from the source, never restored — see `restrictedFor`.
  restrictedOnly: true,
};

/** The restricted filter follows the SOURCE and is deliberately not persisted.

The FTMO source exists to answer one question — what may I not trade around —
so it opens on the answer, every load, rather than on whatever the last session
happened to leave behind. ForexFactory has no restricted marking at all and its
toggle is hidden there, so a remembered `true` would blank that panel with no
visible control to explain it. Turning it off stays a within-session choice. */
function restrictedFor(source: NewsSourceId): boolean {
  return source === "ftmo";
}

interface PersistedPrefs {
  filters: NewsFilters;
  showOnChart: boolean;
}

function readPrefs(): PersistedPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { filters: DEFAULT_FILTERS, showOnChart: true };
    const p = JSON.parse(raw) as Partial<NewsFilters> & {
      showOnChart?: boolean;
    };
    const source = p.source === "forexfactory" ? "forexfactory" : "ftmo";
    return {
      filters: {
        source,
        impact:
          p.impact && ["all", "low", "medium", "high"].includes(p.impact)
            ? (p.impact as ImpactFloor)
            : DEFAULT_FILTERS.impact,
        currencies: Array.isArray(p.currencies)
          ? p.currencies.filter((c) => typeof c === "string")
          : [],
        restrictedOnly: restrictedFor(source),
      },
      showOnChart: p.showOnChart !== false,
    };
  } catch {
    return { filters: DEFAULT_FILTERS, showOnChart: true };
  }
}

function writePrefs(filters: NewsFilters, showOnChart: boolean): void {
  try {
    // `restrictedOnly` is intentionally not stored — it is re-derived from the
    // source on every load (see `restrictedFor`).
    const { restrictedOnly: _omitted, ...stored } = filters;
    localStorage.setItem(
      PREFS_KEY,
      JSON.stringify({ ...stored, showOnChart }),
    );
  } catch {
    // Private mode / quota — prefs just won't persist.
  }
}

interface NewsState {
  payload: NewsPayload | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  filters: NewsFilters;
  /** Whether high-impact events render as vertical lines on the chart. */
  showOnChart: boolean;
}

const boot = readPrefs();

const initialState: NewsState = {
  payload: null,
  loading: false,
  refreshing: false,
  error: null,
  filters: boot.filters,
  showOnChart: boot.showOnChart,
};

/** Read the cached server snapshot. Cheap — the server does the upstream
 *  fetching on its own loop. */
export const fetchNews = createAsyncThunk("news/fetch", async () => api.news());

/** Force the server to refetch upstream (the panel's refresh button). */
export const refreshNews = createAsyncThunk("news/refresh", async () =>
  api.newsRefresh(),
);

const newsSlice = createSlice({
  name: "news",
  initialState,
  reducers: {
    setNewsFilters(state, action: PayloadAction<Partial<NewsFilters>>) {
      const next = { ...state.filters, ...action.payload };
      // Switching source re-derives the restricted filter, the same way a load
      // does, unless the caller set it explicitly in the same patch.
      if (
        action.payload.source !== undefined &&
        action.payload.source !== state.filters.source &&
        action.payload.restrictedOnly === undefined
      ) {
        next.restrictedOnly = restrictedFor(action.payload.source);
      }
      state.filters = next;
      writePrefs(state.filters, state.showOnChart);
    },
    setNewsShowOnChart(state, action: PayloadAction<boolean>) {
      state.showOnChart = action.payload;
      writePrefs(state.filters, state.showOnChart);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNews.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNews.fulfilled, (state, action) => {
        state.loading = false;
        state.payload = action.payload;
        state.error = null;
      })
      .addCase(fetchNews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load news";
      })
      .addCase(refreshNews.pending, (state) => {
        state.refreshing = true;
      })
      .addCase(refreshNews.fulfilled, (state, action) => {
        state.refreshing = false;
        state.payload = action.payload;
        state.error = null;
      })
      .addCase(refreshNews.rejected, (state, action) => {
        state.refreshing = false;
        state.error = action.error.message ?? "Refresh failed";
      });
  },
});

export const { setNewsFilters, setNewsShowOnChart } = newsSlice.actions;
export default newsSlice.reducer;
